import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Spinner } from "@via/components/loading/loading";
import { createUserGroupOptions } from "@via/utils/form-validators";
import { useEffect, type JSX } from "react";
import { useForm } from "react-hook-form";
import { useCreateUserGroup } from "@via/api/user-groups.api.tsx";

export default function CreateUserGroupForm({
  setOpen,
}: {
  setOpen: (open: boolean) => void;
}): JSX.Element {
  const handleClose = () => setOpen(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{
    name: string;
  }>({
    mode: "onChange",
  });

  const {
    mutateAsync: createUserGroup,
    isPending: isCreateUserGroupLoading,
    isSuccess: isCreateUserGroupSuccess,
  } = useCreateUserGroup();

  const handleCreateUserGroup = async (payload: { name: string }) => {
    await createUserGroup(payload);
  };

  useEffect(() => {
    isCreateUserGroupSuccess && handleClose();
  }, [isCreateUserGroupSuccess]);

  return (
    <form
      className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
      onSubmit={e => {
        e.preventDefault();
        void handleSubmit(handleCreateUserGroup)(e);
      }}>
      <div className="h-0 flex-1 overflow-y-auto">
        <div className="border-b bg-gray-50 px-4 py-6 sm:px-6">
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              New User Group
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none"
                onClick={handleClose}>
                <span className="absolute -inset-2.5" />
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="mt-1">
            <p className="text-sm text-gray-500">
              Get started by filling in the information below to create your
              user group.
            </p>
          </div>
        </div>
        <div className="flex flex-1 flex-col justify-between">
          <div className="divide-y divide-gray-200 px-4 sm:px-6">
            <div className="space-y-6 pb-5 pt-6">
              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Name <span className="text-red-400">*</span>
                </label>
                <div className="mt-2">
                  <input
                    {...register("name", createUserGroupOptions.name)}
                    type="text"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="Enter User Group Name"
                  />
                  {errors?.name && (
                    <span className="text-xs text-rose-500">
                      {errors.name.message}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-shrink-0 justify-end px-4 py-4">
        <button
          id="create-user-group-cancel"
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={handleClose}>
          Cancel
        </button>
        <button
          id="create-user-group-save"
          type="submit"
          className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          <Spinner loading={isCreateUserGroupLoading}>Save</Spinner>
        </button>
      </div>
    </form>
  );
}
